import React, { CSSProperties, MouseEventHandler, ReactNode } from "react"
import { Avatar as MaterialAvatar, Badge, BadgeOrigin } from "@mui/material"
import { theme } from "../../../theme"
import { getBadgeColor } from "../../getters"
import styled from "styled-components"
import { LogoDark, Enterprise, Star } from "../../../icons"
import { Tooltip, TooltipWhite } from "../../common/Tooltip"
import { Individual, Organization } from "../../common"
import { formatFullName, formatInitials } from "../../formatters"
import { Edit } from "@mui/icons-material"

const positions = {
  top: {
    vertical: "top",
    horizontal: "right",
  } as BadgeOrigin,
  bottom: {
    vertical: "bottom",
    horizontal: "right",
  } as BadgeOrigin,
}

const identities = {
  enterprise: <Enterprise color={theme.color.black6} fill={theme.color.white} />,
  straumann: <LogoDark color={theme.color.black6} fill={theme.color.white} />,
}

export enum AvatarSize {
  small = "small",
  medium = "medium",
  big = "big",
}

export enum AvatarIdentities {
  enterprise = "enterprise",
  straumann = "straumann",
}

export type AvatarProps = AvatarBaseProps & {
  individual: Individual
  size?: AvatarSize
  identity?: AvatarIdentities
  starredLabel?: string
  extraInfo?: ReactNode
}

export type AvatarOrganizationProps = AvatarBaseProps & {
  organization: Organization
  organizationIcon?: boolean
}

export type AvatarBaseProps = {
  children?: ReactNode
  className?: string // TODO: should became variations
  hasColor?: boolean
  style?: CSSProperties
  customContent?: string
  onClick?: MouseEventHandler<HTMLElement>
}

const renderAvatar = (
  color: string,
  hasColor: boolean,
  size: AvatarSize,
  children: string,
  className?: string,
  style?: CSSProperties,
  avatar?: string
) => {
  return (
    <Root
      $color={!!avatar ? "" : color}
      $hasColor={hasColor}
      $size={size}
      className={className}
      style={style}
      src={avatar}
    >
      <Text $size={size}>{children}</Text>
    </Root>
  )
}

const buildEditBadge = (onClick: MouseEventHandler<HTMLElement>) => (
  <EditWrapper onClick={onClick}>
    <Edit />
  </EditWrapper>
)

export const AvatarOrganization = (props: AvatarOrganizationProps) => {
  const { organization, style, className, hasColor = true, organizationIcon = false, onClick = undefined } = props
  const color = hasColor ? getBadgeColor(organization.name) : theme.color.white
  const size = organizationIcon ? AvatarSize.medium : AvatarSize.big

  const tooltip = (
    <React.Fragment>
      <TooltipTitle>{organization.name}</TooltipTitle>
    </React.Fragment>
  )

  const getBottomBadge = () => {
    return onClick ? (
      buildEditBadge(onClick)
    ) : organizationIcon ? (
      <Enterprise color={theme.color.black6} fill={theme.color.white} />
    ) : undefined
  }

  return (
    <Tooltip title={tooltip} disableHoverListener={!!onClick}>
      <CustomBadge
        anchorOrigin={positions.bottom}
        badgeContent={getBottomBadge()}
        $size={AvatarSize.medium}
        $zIndex={Number(style?.zIndex)}
      >
        {renderAvatar(color, hasColor, size, formatInitials(organization.name), className, style)}
      </CustomBadge>
    </Tooltip>
  )
}

export const Avatar = (props: AvatarProps) => {
  const {
    individual,
    style,
    extraInfo,
    customContent,
    className,
    size = AvatarSize.medium,
    starredLabel,
    hasColor = true,
    identity,
    onClick = undefined,
  } = props
  const starred = starredLabel !== undefined && starredLabel !== ""
  const color = hasColor ? getBadgeColor(individual.lastName) : theme.color.white
  const initials = formatInitials(individual.firstName, individual.lastName)
  const tooltipTitle = formatFullName(individual.firstName, individual.lastName, individual.title)
  const content = customContent
    ? renderAvatar(color, hasColor, size, customContent, className, style)
    : renderAvatar(color, hasColor, size, initials, className, style, individual.avatar)

  let tooltip = extraInfo ? (
    <ExtraInfo>{extraInfo}</ExtraInfo>
  ) : (
    <React.Fragment>
      {starred && (
        <TooltipLegend>
          <Star color={theme.color.grey2} />
          {starredLabel}
        </TooltipLegend>
      )}
      <TooltipTitle>{tooltipTitle}</TooltipTitle>
      <TooltipSubTitle>
        {identity === AvatarIdentities.straumann ? (
          <div>
            <LogoDark color={theme.color.white} fill={"none"} /> Straumann
          </div>
        ) : (
          individual.organization
        )}
      </TooltipSubTitle>
    </React.Fragment>
  )

  const getBottomBadge = () => {
    return onClick ? buildEditBadge(onClick) : identity ? identities[identity] : undefined
  }

  const getAvatar = () => {
    return (
      <CustomBadge
        anchorOrigin={positions.top}
        badgeContent={starred ? <Star color={theme.color.black6} circleColor={theme.color.white} /> : ""}
        $size={size}
        $zIndex={Number(style?.zIndex)}
      >
        <CustomBadge
          anchorOrigin={positions.bottom}
          badgeContent={getBottomBadge()}
          $size={size}
          $zIndex={Number(style?.zIndex)}
        >
          {content}
        </CustomBadge>
      </CustomBadge>
    )
  }

  if (extraInfo) {
    return (
      <TooltipWhite title={tooltip} st>
        {getAvatar()}
      </TooltipWhite>
    )
  }

  return <Tooltip title={tooltip}>{getAvatar()}</Tooltip>
}

const Root = styled(MaterialAvatar)<{ $color?: string; $hasColor: boolean; $size: AvatarSize }>`
  background-color: ${(props) => props.$color};
  color: ${(props) => (props.$color ? theme.color.black6 : "inherit")};
  width: ${(props) => (props.$size ? circleSizes[props.$size] : "")};
  height: ${(props) => (props.$size ? circleSizes[props.$size] : "")};
  font-size: ${(props) => (props.$size === AvatarSize.small ? theme.fontSize.s : theme.fontSize.l)};
  font-family: ${theme.font.bold};
  box-sizing: content-box;
  border: 2px solid ${(props) => (props.$hasColor === true ? "transparent" : theme.color.white1)}; ;
`

const circleSizes = {
  small: theme.circleSize.s,
  medium: theme.circleSize.m,
  big: theme.circleSize.l,
}

const fontSizes = {
  small: theme.fontSize.s,
  medium: theme.fontSize.l,
  big: theme.fontSize.lx,
}

const svgSizes = {
  small: theme.fontSize.s,
  medium: theme.fontSize.m,
  big: theme.fontSize.lx,
}

const Text = styled.div<{ $size?: AvatarSize }>`
  opacity: 0.5;
  font-size: ${(props) => (props.$size ? fontSizes[props.$size] : "")};
`

const CustomBadge = styled(Badge)<{ $size?: AvatarSize; $zIndex?: number }>`
  .MuiBadge-badge {
    z-index: ${(props) => props.$zIndex};
  }

  svg {
    width: ${(props) => (props.$size ? svgSizes[props.$size] : "")};
    height: ${(props) => (props.$size ? svgSizes[props.$size] : "")};
  }

  .MuiBadge-anchorOriginTopLeftRectangular {
    top: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
    left: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
  }

  .MuiBadge-anchorOriginTopRightRectangular {
    top: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
    right: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
  }

  .MuiBadge-anchorOriginBottomLeftRectangular {
    bottom: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
    left: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
  }

  .MuiBadge-anchorOriginBottomRightRectangular {
    bottom: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
    right: ${(props) => (props.$size === AvatarSize.small ? theme.spacing.s : theme.spacing.m)};
  }
`

const TooltipLegend = styled.div`
  color: ${theme.color.grey2};
  font-size: ${theme.fontSize.s};

  svg {
    margin-left: -${theme.spacing.xs};
    margin-right: ${theme.spacing.xs};
    height: 15px;
    width: 15px;
    vertical-align: sub;
  }
`

const TooltipTitle = styled.div`
  font-size: ${theme.fontSize.m};
`

const TooltipSubTitle = styled.div`
  font-size: ${theme.fontSize.s};
`

const ExtraInfo = styled.div`
  white-space: pre-line;
`

const EditWrapper = styled.div`
  background: ${theme.color.primary};
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  justify-content: center;

  > svg {
    align-self: center;
    width: 12px;
    height: 12px;
  }

  &:hover {
    cursor: pointer;
  }
`
